import { formatUsd, type DriftSummary } from '@/features/projects/rollup-bom'
import { cn } from '@/lib/utils'

interface DriftStripProps {
  drift: DriftSummary
}

/**
 * Phase 12: doing-view header strip showing committed-vs-acquired-vs-still-needed-vs-drift.
 * Renders nothing when {@code startingTotalCents} is null (pre-Phase-12 followings; no
 * baseline captured). Drift de-emphasized when no acquisitions yet.
 */
export const DriftStrip = ({ drift }: DriftStripProps) => {
  if (drift.startingTotalCents == null) return null
  const noAcquisitionsYet = drift.acquiredTotalCents === 0
  const driftPositive = drift.driftCents != null && drift.driftCents > 0
  const driftNegative = drift.driftCents != null && drift.driftCents < 0

  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <Stat label="Committed" value={formatUsd(drift.startingTotalCents)} />
        <Stat
          label="Acquired"
          value={formatUsd(drift.acquiredTotalCents)}
          dim={noAcquisitionsYet}
        />
        <Stat label="Still need" value={formatUsd(drift.stillNeededCents)} />
        {drift.driftCents != null && drift.driftCents !== 0 && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs tabular-nums',
              driftPositive && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
              driftNegative && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
            )}
            title="Drift = (acquired + still-need) − committed at start"
          >
            {drift.driftCents > 0 ? '+' : ''}
            {formatUsd(drift.driftCents)} drift
          </span>
        )}
      </div>
    </div>
  )
}

const Stat = ({ label, value, dim }: { label: string; value: string; dim?: boolean }) => (
  <span className={cn('inline-flex items-baseline gap-1', dim && 'text-muted-foreground')}>
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    <span className="tabular-nums">{value}</span>
  </span>
)
